import React from "react";
import { useTranslation } from "react-i18next";
import DetailedTrainingView from "./DetailedTrainingView";
import "../styles/Certificate.css";

interface CertificateProps {
  data: {
    firstName: string;
    lastName: string;
    trainingType: string;
    trainingTime: any;
    trainingResult: any;
    trainingDurationFormatted?: any;
    appVersion?: string;
    sensorUsed?: boolean;
    certificateCode?: string;
    headquarterName?: string;
  };
}

const formatDateDDMMYYYY = (timestampInput: any): string => {
  const timestampMs = Number(timestampInput);
  if (!isNaN(timestampMs) && timestampMs > 0) {
    const date = new Date(timestampMs);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  }
  return "Invalid Date";
};

const formatDuration = (durationString, t) => {
  if (!durationString || !/^\d{2}:\d{2}:\d{2}$/.test(durationString)) {
    return `5${t("minutes_short")}`;
  }

  const parts = durationString.split(":");
  const hours = parseInt(parts[0], 10);
  const minutes = parseInt(parts[1], 10);
  const seconds = parseInt(parts[2], 10);

  const resultParts = [];

  if (hours > 0) {
    resultParts.push(`${hours}${t("hours_short")}`);
  }

  if (minutes > 0) {
    resultParts.push(`${minutes}${t("minutes_short")}`);
  }

  if (seconds > 0) {
    resultParts.push(`${seconds}${t("seconds_short")}`);
  }

  if (resultParts.length === 0) {
    return `0${t("seconds_short")}`;
  }

  return resultParts.join(" ");
};

const Certificate: React.FC<CertificateProps> = ({ data }) => {
  const { t } = useTranslation();

  let trainingResultObject = null;
  if (data.trainingResult) {
    if (typeof data.trainingResult === "string") {
      try {
        trainingResultObject = JSON.parse(data.trainingResult);
      } catch (error) {
        console.error("Invalid JSON string for certificate:", error);
      }
    } else if (typeof data.trainingResult === "object") {
      trainingResultObject = data.trainingResult;
    }
  }

  const hasDetailedResults =
    trainingResultObject && trainingResultObject.meanRate !== undefined;

  return (
    <div className="certificate-layout">
      {/* Left Column */}
      <div className="certificate-left">
        <h1 className="main-title">{t("CERTIFICATE OF GRADUATION")}</h1>
        <div className="description-centered">
          {t("Hereby we confirm that")}{" "}
          <div className="username-inline">
            {/*data.firstName*/} {/*data.lastName*/}
          </div>{" "}
          {t("successfully graduated")}
          <div className="cpr-title-inline">
            {t("interactive training cardiopulmonary resuscitation")}
          </div>
          {t("in simulated environment virtual reality")}
        </div>
        <p className="skills-title">
          {t("During training were monitored and evaluated following skills:")}
        </p>
        {hasDetailedResults && (
          <div className="detailed-view-wrapper">
            <DetailedTrainingView details={{
              trainingResult: trainingResultObject,
              sensorUsed: data.sensorUsed ?? false
            }}
            />
          </div>
        )}
      </div>


      {/* Right Column */}
      <div className="certificate-right">
        <div className="positioned-container">
          <img
            src="/images/results/Banner_Top.png"
            alt="Banner"
            className="background-image"
          />
          <div className="content-overlay-top">
            <img
              src="/images/results/Logo_Banner_Top.png"
              alt="VR CPR Logo"
              className="cpr-logo-img"
            />
          </div>
        </div>
        <div className="right-middle-content">
          <ul className="details-list">
            <li>
              <img src="/images/results/Icon_GraduationDate.png" alt="Date" />
              <div>
                <p className="detail-label">{t("Graduation date")}</p>
                <p className="detail-value">
                  {formatDateDDMMYYYY(data.trainingTime)}
                </p>
              </div>
            </li>
            <li>
              <img
                src="/images/results/Icon_TrainingDuration.png"
                alt="Duration"
              />
              <div>
                <p className="detail-label">{t("Training duration")}</p>
                <p className="detail-value">
                  {formatDuration(data.trainingDurationFormatted, t)}
                </p>
              </div>
            </li>
            <li>
              <img
                src="/images/results/Icon_VersionApplication.png"
                alt="Version"
              />
              <div>
                <p className="detail-label">{t("Version application")}</p>
                <p className="detail-value">{data.appVersion || "0.37"}</p>
              </div>
            </li>
            <li>
              <img src="/images/results/Icon_CodeCertificate.png" alt="Code" />
              <div>
                <p className="detail-label">{t("Code certificate")}</p>
                <p className="detail-value">
                  {data.certificateCode || "CPR-unique-code"}
                </p>
              </div>
            </li>
          </ul>
        </div>

        <div className="positioned-container">
          <img
            src="/images/results/Banner_Bottom.png"
            alt="Footer Banner"
            className="background-image"
          />
          <div className="content-overlay-bottom">
            <div className="issuer-wrapper">
              <p className="issuer-label">{t("Certificate issued by")}</p>
              <p className="issuer-name">
                {data.headquarterName || "[Headquarter name]"}
              </p>
              <div className="issuer-divider" />
              <img
                src="/images/results/Logo_Banner_Bottom.png"
                alt="Virtual Lab Logo"
                className="lab-logo"
              />
            </div>
          </div>
        </div>
      </div>
      <div className="certificate-right-2">
        <h1 className="main-title"></h1>
      </div>
    </div>
  );
};

export default Certificate;
